import { useState, useEffect, useCallback } from 'react';
import './ToolStyles.css';

interface ExchangeRates {
    [key: string]: number;
}

const currencies = [
    { code: 'USD', name: '美元', symbol: '$' },
    { code: 'EUR', name: '欧元', symbol: '€' },
    { code: 'GBP', name: '英镑', symbol: '£' },
    { code: 'JPY', name: '日元', symbol: '¥' },
    { code: 'CNY', name: '人民币', symbol: '¥' },
    { code: 'KRW', name: '韩元', symbol: '₩' },
    { code: 'HKD', name: '港币', symbol: 'HK$' },
    { code: 'AUD', name: '澳元', symbol: 'A$' },
    { code: 'CAD', name: '加元', symbol: 'C$' },
    { code: 'SGD', name: '新加坡元', symbol: 'S$' },
];

// 接口不可用时的离线参考汇率（以 USD 为基准），仅作兜底
const FALLBACK_RATES: ExchangeRates = {
    USD: 1,
    EUR: 0.92,
    GBP: 0.79,
    JPY: 149.5,
    CNY: 7.24,
    KRW: 1320.5,
    HKD: 7.82,
    AUD: 1.52,
    CAD: 1.36,
    SGD: 1.34,
};

const CurrencyConverter = () => {
    const [amount, setAmount] = useState('100');
    const [fromCurrency, setFromCurrency] = useState('USD');
    const [toCurrency, setToCurrency] = useState('CNY');
    const [rates, setRates] = useState<ExchangeRates>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [lastUpdate, setLastUpdate] = useState('');
    const [isOffline, setIsOffline] = useState(false);

    const fetchRates = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            // 免费、无需 API key 的实时汇率接口（以 USD 为基准）
            const response = await fetch('https://open.er-api.com/v6/latest/USD');
            if (!response.ok) throw new Error('网络请求失败');

            const data = await response.json();
            if (data.result !== 'success' || !data.rates) {
                throw new Error('返回数据异常');
            }

            setRates(data.rates as ExchangeRates);
            setIsOffline(false);

            const updatedAt = data.time_last_update_unix
                ? new Date(data.time_last_update_unix * 1000)
                : new Date();
            setLastUpdate(updatedAt.toLocaleString('zh-CN'));
        } catch (err) {
            // 接口失败时回退到内置参考汇率
            setRates(FALLBACK_RATES);
            setIsOffline(true);
            setLastUpdate('');
            setError('实时汇率获取失败，已切换到离线参考汇率（可能不准确）');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRates();
    }, [fetchRates]);

    const swapCurrencies = () => {
        setFromCurrency(toCurrency);
        setToCurrency(fromCurrency);
    };

    const convert = (): string => {
        if (!amount || isNaN(Number(amount))) return '0.00';

        const fromRate = rates[fromCurrency];
        const toRate = rates[toCurrency];
        if (!fromRate || !toRate) return '—';

        // 以 USD 为基准做交叉换算
        const result = (Number(amount) / fromRate) * toRate;
        return result.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const unitRate = (): string => {
        const fromRate = rates[fromCurrency];
        const toRate = rates[toCurrency];
        if (!fromRate || !toRate) return '';
        const rate = toRate / fromRate;
        return `1 ${fromCurrency} = ${rate.toFixed(4)} ${toCurrency}`;
    };

    return (
        <div className="tool-container">
            <div className="input-group">
                <label className="input-label">金额</label>
                <input
                    type="number"
                    className="tool-input"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="输入金额"
                />
            </div>

            <div className="currency-row">
                <div className="input-group">
                    <label className="input-label">源货币</label>
                    <select
                        className="tool-select"
                        value={fromCurrency}
                        onChange={(e) => setFromCurrency(e.target.value)}
                    >
                        {currencies.map((curr) => (
                            <option key={curr.code} value={curr.code}>
                                {curr.symbol} {curr.name} ({curr.code})
                            </option>
                        ))}
                    </select>
                </div>

                <button
                    className="swap-button"
                    onClick={swapCurrencies}
                    aria-label="交换货币"
                    title="交换源货币与目标货币"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="17 1 21 5 17 9" />
                        <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                        <polyline points="7 23 3 19 7 15" />
                        <path d="M21 13v2a4 4 0 0 1-4 4H3" />
                    </svg>
                </button>

                <div className="input-group">
                    <label className="input-label">目标货币</label>
                    <select
                        className="tool-select"
                        value={toCurrency}
                        onChange={(e) => setToCurrency(e.target.value)}
                    >
                        {currencies.map((curr) => (
                            <option key={curr.code} value={curr.code}>
                                {curr.symbol} {curr.name} ({curr.code})
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="output-group">
                <label className="output-label">转换结果</label>
                <div className="tool-output large">
                    {loading ? '加载中…' : `${convert()} ${toCurrency}`}
                </div>
                {!loading && unitRate() && (
                    <div className="info-text">{unitRate()}</div>
                )}
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="rate-status">
                <span className="info-text">
                    {isOffline
                        ? '数据来源：离线参考汇率'
                        : lastUpdate
                            ? `实时汇率 · 更新于 ${lastUpdate}`
                            : ''}
                </span>
                <button
                    className="tool-button"
                    onClick={fetchRates}
                    disabled={loading}
                >
                    {loading ? '更新中…' : '刷新汇率'}
                </button>
            </div>

            <div className="info-box">
                <h4>说明：</h4>
                <ul>
                    <li>汇率来自 open.er-api.com 实时接口，每日更新</li>
                    <li>以美元为基准进行交叉换算，支持主流货币双向转换</li>
                    <li>接口不可用时自动切换到内置离线汇率</li>
                    <li>汇率会实时波动，仅供参考，请以实际交易为准</li>
                </ul>
            </div>
        </div>
    );
};

export default CurrencyConverter;
